import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { adminApi, API } from '../api';
import { useAuth } from '../AuthContext';
import { TextInput, I18nInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-paelbi-line bg-white p-6">
    <h3 className="font-display text-lg text-paelbi-ink">{title}</h3>
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

function computeMergedTheme(t, s) {
  return {
    ...t.theme,
    brown: s.theme?.primary || t.theme?.brown,
    bg2: s.theme?.secondary || t.theme?.bg2,
    accent: s.theme?.accent || t.theme?.accent,
  };
}

const THEME_KEYS = [['primary', 'Primario'], ['secondary', 'Secondario'], ['accent', 'Accento']];

function ThemeSection({ theme, setSec }) {
  return (
    <Section title="Tema (colori)">
      {THEME_KEYS.map(([k, l]) => (
        <div key={k} className="flex items-center gap-3">
          <input type="color" value={theme[k] || '#8B6F4E'} onChange={(e) => setSec('theme', { [k]: e.target.value })} className="h-9 w-12 rounded border border-paelbi-line" />
          <span className="text-sm text-paelbi-ink/70">{l}</span>
          <input value={theme[k] || ''} onChange={(e) => setSec('theme', { [k]: e.target.value })} className="ml-auto w-28 rounded-lg border border-paelbi-line px-2 py-1 text-sm" />
        </div>
      ))}
    </Section>
  );
}

function QrSection({ qrUrl, onRegen }) {
  return (
    <Section title="QR Code struttura">
      <p className="text-sm text-paelbi-ink/60">Apre la homepage della tua struttura. Stampalo e mettilo in camera.</p>
      {qrUrl && <img src={qrUrl} alt="QR struttura" className="h-40 w-40 rounded-lg border border-paelbi-line" />}
      <button type="button" data-testid="regen-qr-button" onClick={onRegen} className="inline-flex items-center gap-2 rounded-btn border border-paelbi-line px-4 py-2 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50">
        <RefreshCw size={15} /> Rigenera QR
      </button>
    </Section>
  );
}

function HomepageSection({ homepage, setSec }) {
  const visible = homepage?.button_visibility?.mySoggiorno !== false;
  return (
    <Section title="Homepage">
      <label className="flex items-center gap-2 text-sm text-paelbi-ink/80">
        <input type="checkbox" checked={visible}
          onChange={(e) => setSec('homepage', { button_visibility: { ...(homepage?.button_visibility || {}), mySoggiorno: e.target.checked } })} />
        Mostra pulsante "Il mio soggiorno"
      </label>
      <p className="text-xs text-paelbi-ink/50">Hero, titoli e banner della homepage si gestiscono in "Home ospite" e "Banner".</p>
    </Section>
  );
}

export function CustomizeProperty() {
  const { tenantId } = useAuth();
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { if (tenantId) adminApi.getTenant(tenantId).then(setT); }, [tenantId]);
  if (!t) return <p className="text-sm text-paelbi-ink/50">Seleziona una struttura...</p>;

  const s = t.settings || {};
  const setSec = (sec, patch) => setT({ ...t, settings: { ...s, [sec]: { ...(s[sec] || {}), ...patch } } });

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await adminApi.updateTenant(t.id, { settings: t.settings, theme: computeMergedTheme(t, s), logo_url: t.logo_url });
      setMsg('Salvato');
    } catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const regenQr = async () => {
    const r = await adminApi.regeneratePropertyQr(tenantId);
    setT({ ...t, qr_token: r.qr_token });
  };

  const qrUrl = t.qr_token ? `${API}/qr/property/${t.qr_token}.png` : null;

  return (
    <div>
      <h1 className="font-display text-3xl text-paelbi-ink">Personalizza struttura</h1>
      <p className="mt-1 text-paelbi-ink/60">Configura ogni aspetto della tua struttura senza toccare il codice.</p>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Section title="Generale">
          <TextInput label="Nome struttura" value={t.brand?.name} onChange={(v) => setT({ ...t, brand: { ...t.brand, name: v } })} />
          <I18nInput label="Payoff" value={t.brand?.payoff} onChange={(v) => setT({ ...t, brand: { ...t.brand, payoff: v } })} />
          <I18nInput label="Messaggio di benvenuto" value={s.general?.welcome_message} multiline onChange={(v) => setSec('general', { welcome_message: v })} />
          <ImagePicker label="Logo" value={t.logo_url} tenantId={tenantId} onChange={(v) => setT({ ...t, logo_url: v })} />
          <ImagePicker label="Favicon" value={s.general?.favicon} tenantId={tenantId} onChange={(v) => setSec('general', { favicon: v })} />
        </Section>

        <Section title="Contatti">
          <TextInput label="Indirizzo" value={s.contact?.address} onChange={(v) => setSec('contact', { address: v })} />
          <TextInput label="Telefono" value={s.contact?.phone} onChange={(v) => setSec('contact', { phone: v })} />
          <TextInput label="Email" value={s.contact?.email} onChange={(v) => setSec('contact', { email: v })} />
          <TextInput label="Sito web" value={s.contact?.website} onChange={(v) => setSec('contact', { website: v })} />
        </Section>

        <Section title="Mappa (Google Maps ready)">
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Latitudine" value={s.map?.lat} onChange={(v) => setSec('map', { lat: v })} />
            <TextInput label="Longitudine" value={s.map?.lng} onChange={(v) => setSec('map', { lng: v })} />
          </div>
          <TextInput label="Place ID" value={s.map?.place_id} onChange={(v) => setSec('map', { place_id: v })} />
          <TextInput label="Google Maps API Key" value={s.map?.api_key} onChange={(v) => setSec('map', { api_key: v })} />
          <TextInput label="Zoom" value={s.map?.zoom} onChange={(v) => setSec('map', { zoom: v })} />
          <label className="flex items-center gap-2 text-sm text-paelbi-ink/80">
            <input type="checkbox" checked={!!s.map?.marker} onChange={(e) => setSec('map', { marker: e.target.checked })} /> Mostra marker
          </label>
        </Section>

        <Section title="WiFi">
          <TextInput label="Nome rete (SSID)" value={s.wifi?.ssid} onChange={(v) => setSec('wifi', { ssid: v })} />
          <TextInput label="Password" value={s.wifi?.password} onChange={(v) => setSec('wifi', { password: v })} />
        </Section>

        <Section title="Check-in / Check-out">
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Check-in" value={s.checkin?.checkin_time} onChange={(v) => setSec('checkin', { checkin_time: v })} />
            <TextInput label="Check-out" value={s.checkin?.checkout_time} onChange={(v) => setSec('checkin', { checkout_time: v })} />
          </div>
          <I18nInput label="Istruzioni self check-in" value={s.checkin?.instructions} multiline onChange={(v) => setSec('checkin', { instructions: v })} />
          <TextInput label="Codice di accesso" value={s.checkin?.access_code} onChange={(v) => setSec('checkin', { access_code: v })} />
        </Section>

        <ThemeSection theme={s.theme || {}} setSec={setSec} />
        <QrSection qrUrl={qrUrl} onRegen={regenQr} />
        <HomepageSection homepage={s.homepage} setSec={setSec} />
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button type="button" data-testid="customize-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva personalizzazione'}</button>
        {msg && <span className={`text-sm ${msg.startsWith('Errore') ? 'text-red-600' : 'text-green-700'}`}>{msg}</span>}
      </div>
    </div>
  );
}

export default CustomizeProperty;
